'use client'

import { useMemo } from 'react'
import type { CardInfo } from '@/types/collections'
import { Badge } from '@/components/ui/badge'
import { MutedText } from '@/components/ui/typography'
import {
  IconClock,
  IconRefresh,
  IconTrendingUp,
  IconAlertCircle
} from '@tabler/icons-react'
import { cn } from '@/lib/utils'

function formatTimeAgo(date: string | null): string | null {
  if (!date) return null

  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

  // For longer periods, show formatted date
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(diffDays > 365 && { year: 'numeric' })
  }).format(then)
}

function formatDueTime(
  due: string,
  isNew: boolean
): {
  label: string
  isOverdue: boolean
  isSoon: boolean
} {
  const dueDate = new Date(due)
  const now = new Date()
  const diffMs = dueDate.getTime() - now.getTime()

  // For new cards (state === 0), don't show "Overdue" - show due date instead
  if (diffMs <= 0) {
    if (isNew) {
      // Show the due date for new cards
      const formattedDate = new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric'
      }).format(dueDate)
      return { label: `Due ${formattedDate}`, isOverdue: false, isSoon: false }
    }
    return { label: 'Overdue', isOverdue: true, isSoon: false }
  }

  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  let label: string
  if (diffMins < 60) {
    label = `Due in ${diffMins} min`
  } else if (diffHours < 24) {
    label = `Due in ${diffHours}h`
  } else if (diffDays < 7) {
    label = `Due in ${diffDays}d`
  } else {
    label = `Due ${new Date(due).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
  }

  return { label, isOverdue: false, isSoon: diffHours < 24 }
}

interface CardInfoBadgeProps {
  cardInfo: CardInfo
  className?: string
}

export function CardInfoBadge({ cardInfo, className }: CardInfoBadgeProps) {
  const { due, last_review, reps, lapses, state } = cardInfo

  // Check if card is new (state === 0)
  const isNew = state === 0

  const dueInfo = useMemo(() => formatDueTime(due, isNew), [due, isNew])
  const lastReviewLabel = useMemo(
    () => formatTimeAgo(last_review),
    [last_review]
  )

  const stateLabel = useMemo(() => {
    if (state === 0) return 'New'
    if (state === 1) return 'Learning'
    if (state === 2) return 'Review'
    if (state === 3) return 'Relearning'
    return 'Unknown'
  }, [state])

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Main stats row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Due Date - Most prominent (but not for new cards) */}
        {!isNew && (
          <Badge
            variant={
              dueInfo.isOverdue
                ? 'destructive'
                : dueInfo.isSoon
                  ? 'default'
                  : 'outline'
            }
            size="sm"
            className="gap-1.5 font-medium"
            title={`Due: ${new Date(due).toLocaleString()}`}
          >
            <IconClock className="h-3.5 w-3.5" />
            <span>{dueInfo.label}</span>
          </Badge>
        )}

        {/* Last Review */}
        {lastReviewLabel && (
          <Badge variant="outline" size="sm" className="gap-1.5">
            <IconRefresh className="h-3.5 w-3.5" />
            <span>Reviewed {lastReviewLabel}</span>
          </Badge>
        )}

        {/* Reps - Clear label */}
        {reps > 0 && (
          <Badge variant="outline" size="sm" className="gap-1.5">
            <IconTrendingUp className="h-3.5 w-3.5" />
            <span>
              {reps} review{reps !== 1 ? 's' : ''}
            </span>
          </Badge>
        )}

        {/* Lapses - Only if > 0, with warning style */}
        {lapses > 0 && (
          <Badge
            variant="outline"
            size="sm"
            className="gap-1.5 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
          >
            <IconAlertCircle className="h-3.5 w-3.5" />
            <span>
              Forgot {lapses} time{lapses !== 1 ? 's' : ''}
            </span>
          </Badge>
        )}
      </div>

      {/* Secondary info - State */}
      <div className="flex items-center gap-2">
        <MutedText className="text-xs">
          Status:{' '}
          <span className="text-foreground font-medium">{stateLabel}</span>
        </MutedText>
      </div>
    </div>
  )
}
